export enum Storages {
  local,
  cookie,
  session,
  memory,
}

const DEFAULT_COOKIE_MAX_AGE_SECONDS = 31_536_000

const cookie = {
  getItem: (name: string): string | null =>
    (document?.cookie || '').split(';').reduce(
      (acc: Record<string, string | null>, curr: string) => ({
        ...acc,
        [curr.split('=')[0].trim()]: curr.split('=')[1]?.trim() || null,
      }),
      {},
    )?.[name] || null,
  setItem: (name: string, value: unknown, maxAgeSeconds: number) => {
    if (!Number.isSafeInteger(maxAgeSeconds) || maxAgeSeconds < 0) {
      throw new RangeError('Cookie max-age must be a non-negative safe integer')
    }

    document.cookie = `${name}=${JSON.stringify(value)}; SameSite=Lax; Secure; Path=/; Max-Age=${maxAgeSeconds}`
  },
  removeItem: (name: string) => {
    document.cookie = `${name}=; SameSite=Lax; Secure; Path=/; Max-Age=0`
  },
}

const _memory: Record<string, unknown> = {}
const memory = {
  getItem: <T>(name: string): T => {
    return (_memory?.[name] || '') as T
  },
  setItem: (name: string, value: unknown) => {
    _memory[name] = value
  },
  removeItem: (name: string) => {
    delete _memory[name]
  },
}

function checkStorage(requestedStorage: Storages) {
  switch (requestedStorage) {
    case Storages.cookie:
      if (document?.cookie)
        return Storages.cookie
      else if (localStorage?.getItem !== null)
        return Storages.local
      else return Storages.memory
    case Storages.local:
      if (localStorage?.getItem !== null)
        return Storages.local
      else if (document?.cookie)
        return Storages.cookie
      else return Storages.memory
    case Storages.session:
      if (sessionStorage?.getItem !== null)
        return Storages.session
      else return Storages.memory
    default:
      return Storages.memory
  }
}

export const storageHelper = {
  getItem: <T>(name: string, storage: Storages = Storages.local): T | null => {
    const availableStorage = checkStorage(storage)
    let storedData: string | null
    switch (availableStorage) {
      case Storages.cookie:
      case Storages.local:
        storedData = (
          availableStorage === Storages.cookie ? cookie : localStorage
        )?.getItem(name) ?? null
        if (!storedData)
          return null
        try {
          return JSON.parse(storedData) as T
        }
        catch {
          // Do nothing
        }
        return storedData as T
      case Storages.session:
        storedData = sessionStorage?.getItem(name) ?? null
        if (!storedData)
          return null
        return storedData as T
      default:
        storedData = memory?.getItem(name)
        if (!storedData)
          return null
        return storedData as T
    }
  },

  setItem: (
    name: string,
    value: unknown,
    storage: Storages = Storages.local,
    maxAgeSeconds = DEFAULT_COOKIE_MAX_AGE_SECONDS,
  ) => {
    const availableStorage = checkStorage(storage)
    switch (availableStorage) {
      case Storages.cookie:
        cookie?.setItem(name, value, maxAgeSeconds)
        break
      case Storages.local:
        window.localStorage?.setItem(
          name,
          ['string', 'boolean', 'number'].includes(typeof value)
            ? value!.toString()
            : JSON.stringify(value),
        )
        break
      case Storages.session:
        sessionStorage.setItem(
          name,
          ['string', 'boolean', 'number'].includes(typeof value)
            ? value!.toString()
            : JSON.stringify(value),
        )
        break
      default:
        memory?.setItem(name, value)
        break
    }
  },

  /**
   * Remove all the session storage items that match the given name pattern
   *
   *
   * @param namePattern - The keys pattern you want to delete from the session storage
   */
  removeItemsFromSession: (namePattern: string) => {
    const allSessionKeys = Object.keys(sessionStorage)
    allSessionKeys.forEach((name) => {
      if (name.includes(namePattern)) {
        storageHelper.removeItem(name, Storages.session)
      }
    })
  },

  removeItem: (name: string, storage: Storages = Storages.local) => {
    const availableStorage = checkStorage(storage)
    switch (availableStorage) {
      case Storages.cookie:
        cookie?.removeItem(name)
        break
      case Storages.local:
        localStorage?.removeItem(name)
        break
      case Storages.session:
        sessionStorage?.removeItem(name)
        break
      default:
        memory?.removeItem(name)
        break
    }
  },
}
